import React from 'react';
import { Button, Divider, Radio, Switch, Tooltip } from 'antd';
import { setDensity } from '../utils/theme';
import useUiPrefs from '../hooks/useUiPrefs';
import { ArrowDownOutlined, ArrowUpOutlined, HolderOutlined } from '@ant-design/icons';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

// 250-1000 added for the CT export/analysis workflows (Aug 17) — page size
// is governed HERE, under the gear, never by a per-table bottom selector.
const PAGE_SIZE_OPTIONS = [10, 15, 25, 50, 100, 250, 500, 1000];

/**
 * Inline column configuration editor — visibility toggle + reorder.
 *
 * Used both by `ColumnConfigDrawer` (per-user settings, AntD Drawer) and by
 * `PartnerColumnDefaultsModal` (admin-side per-partner defaults).
 *
 * @param {Array} props.columns - All columns in current order
 * @param {Set<string>} props.hidden - Hidden column keys
 * @param {Array<string>} props.fixedKeys - Column keys that can't be hidden/reordered
 * @param {Function} props.onToggle - (key) => void
 * @param {Function} props.onMove - (fromIndex, toIndex) => void
 * @param {number} [props.pageSize]
 * @param {Function} [props.onPageSizeChange]
 * @param {boolean} [props.hidePageSize]
 * @param {string} [props.droppableId='column-config'] - Unique id when multiple editors share a page
 */
export default function ColumnConfigEditor({
  columns,
  hidden,
  fixedKeys,
  onToggle,
  onMove,
  pageSize,
  onPageSizeChange,
  hidePageSize,
  droppableId = 'column-config',
}) {
  // Level-3 restyle — table density is a per-browser preference (like page
  // size), so it lives under the same gear. Hidden with page size (the
  // admin per-partner defaults modal reuses this editor).
  const { density } = useUiPrefs();
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const fromIndex = result.source.index;
    const toIndex = result.destination.index;
    if (fromIndex === toIndex) return;
    onMove(fromIndex, toIndex);
  };

  return (
    <div>
      {!hidePageSize && (
        <>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Density</label>
            <Radio.Group value={density} onChange={(e) => setDensity(e.target.value)} size="small">
              <Radio.Button value="comfortable">Comfortable</Radio.Button>
              <Radio.Button value="compact">Compact</Radio.Button>
            </Radio.Group>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Page size</label>
            <Radio.Group value={pageSize} onChange={(e) => onPageSizeChange(e.target.value)} size="small">
              {PAGE_SIZE_OPTIONS.map((size) => (
                <Radio.Button key={size} value={size}>
                  {size}
                </Radio.Button>
              ))}
            </Radio.Group>
          </div>
          <Divider style={{ margin: '16px 0 8px' }} />
        </>
      )}
      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Columns</label>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId={droppableId}>
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              {/* Up/Down enabled if ANY non-fixed column exists on that side.
                  The old "immediate neighbor must not be fixed" rule trapped
                  rows sandwiched between two fixed columns — e.g. Pickup Name
                  appended between `status` and `action` had both arrows greyed
                  out. */}
              {columns.map((col, index) => {
                const key = col.key;
                const isFixed = fixedKeys.includes(key);
                const isHidden = hidden.has(key);
                let canMoveUp = false;
                let canMoveDown = false;
                if (!isFixed) {
                  for (let i = 0; i < index; i++) {
                    if (!fixedKeys.includes(columns[i]?.key)) {
                      canMoveUp = true;
                      break;
                    }
                  }
                  for (let i = index + 1; i < columns.length; i++) {
                    if (!fixedKeys.includes(columns[i]?.key)) {
                      canMoveDown = true;
                      break;
                    }
                  }
                }

                return (
                  <Draggable key={key} draggableId={key} index={index} isDragDisabled={isFixed}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '8px 4px',
                          borderRadius: 4,
                          backgroundColor: snapshot.isDragging ? '#e6f4ff' : 'transparent',
                          borderBottom: '1px solid #f0f0f0',
                          cursor: isFixed ? 'default' : 'grab',
                          ...provided.draggableProps.style,
                        }}
                      >
                        <span
                          {...provided.dragHandleProps}
                          style={{ color: isFixed ? '#d9d9d9' : '#999', cursor: isFixed ? 'default' : 'grab' }}
                        >
                          <HolderOutlined />
                        </span>
                        <span style={{ flex: 1, fontSize: 14, color: isHidden ? '#bfbfbf' : '#333' }}>
                          {typeof col.title === 'string' || !col.title
                            ? col.title || key
                            : // Element titles (e.g. a Tooltip-wrapped header) can't render
                              // in the drawer — columns provide a plain `configLabel` instead.
                              col.configLabel || key}
                        </span>
                        {!isFixed && (
                          <>
                            <Tooltip title="Move up">
                              <Button
                                type="text"
                                size="small"
                                disabled={!canMoveUp}
                                icon={<ArrowUpOutlined />}
                                onClick={() => {
                                  let target = index - 1;
                                  while (target >= 0 && fixedKeys.includes(columns[target]?.key)) target--;
                                  if (target >= 0) onMove(index, target);
                                }}
                                style={{ padding: '0 4px' }}
                              />
                            </Tooltip>
                            <Tooltip title="Move down">
                              <Button
                                type="text"
                                size="small"
                                disabled={!canMoveDown}
                                icon={<ArrowDownOutlined />}
                                onClick={() => {
                                  let target = index + 1;
                                  while (target < columns.length && fixedKeys.includes(columns[target]?.key)) target++;
                                  if (target < columns.length) onMove(index, target);
                                }}
                                style={{ padding: '0 4px' }}
                              />
                            </Tooltip>
                          </>
                        )}
                        <Switch size="small" checked={!isHidden} disabled={isFixed} onChange={() => onToggle(key)} />
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
}
